import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';

/**
 * Champ d'autocomplétion d'adresses françaises.
 * Utilise l'API gratuite du gouvernement (api-adresse.data.gouv.fr).
 *
 * Props :
 *   value       : string           - Valeur actuelle du champ
 *   onChange    : (value) => void  - Callback appelé à chaque changement
 *   onSelect    : (feature) => void- (optionnel) Callback quand une adresse est sélectionnée
 *                                    Reçoit l'objet complet : { label, postcode, city, department, ... }
 *   id, required, placeholder, className : props HTML standards
 */
const AddressAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  id = 'address',
  required = false,
  placeholder = 'Commencez à taper une adresse...',
  className = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextFetch = useRef(false);

  // Fermer la liste au clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce + fetch API adresse
  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const resp = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(
            value
          )}&limit=6&autocomplete=1`
        );
        if (!resp.ok) throw new Error('API error');
        const data = await resp.json();
        const feats = (data.features || []).map((f) => {
          const p = f.properties || {};
          // context : "75, Paris, Île-de-France"
          const ctxParts = (p.context || '').split(',').map((s) => s.trim());
          const departmentCode = ctxParts[0] || '';
          const departmentName = ctxParts[1] || '';
          const region = ctxParts[2] || '';
          return {
            label: p.label || '',
            name: p.name || '',
            postcode: p.postcode || '',
            city: p.city || '',
            departmentCode,
            departmentName,
            region,
            coordinates: (f.geometry && f.geometry.coordinates) || null,
          };
        });
        setSuggestions(feats);
        setIsOpen(feats.length > 0);
        setHighlightedIdx(-1);
      } catch (err) {
        // silencieux — on garde juste l'input normal
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const handleSelect = (feat) => {
    skipNextFetch.current = true;
    onChange(feat.label);
    if (typeof onSelect === 'function') onSelect(feat);
    setIsOpen(false);
    setSuggestions([]);
    setHighlightedIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        required={required}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        data-testid="address-autocomplete-input"
      />

      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Loader2 size={16} className="animate-spin text-gray-400" />
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid="address-suggestions"
        >
          {suggestions.map((feat, idx) => (
            <li
              key={`${feat.label}-${idx}`}
              onMouseDown={(e) => {
                // mousedown pour éviter que onBlur ferme la liste avant le clic
                e.preventDefault();
                handleSelect(feat);
              }}
              onMouseEnter={() => setHighlightedIdx(idx)}
              className={`px-3 py-2 cursor-pointer flex items-start gap-2 text-sm ${
                idx === highlightedIdx ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
            >
              <MapPin
                size={16}
                className="mt-0.5 flex-shrink-0 text-green-600"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {feat.name || feat.label}
                </div>
                <div className="text-xs text-gray-500">
                  {feat.postcode} {feat.city}
                  {feat.departmentName && (
                    <span className="ml-1">
                      — {feat.departmentCode} {feat.departmentName}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
